import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  BillingInformation,
  BillingMethod,
  Invoice,
  Plan,
  useFinalizeInvoice,
} from '@app/api';
import { useToast, SubmitButton } from '@app/core';
import { useTranslation } from 'react-i18next';
import { SubmitButtonsLine, Toggle } from 'components';
import { Form, Formik, FormikConsumer } from 'formik';
import { ExtraBillingEmailsField } from './ExtraBillingEmailsField';

const useStyles = createUseStyles((theme: Theme) => ({
  sectionTitle: {
    ...theme.fonts.h4,
    marginTop: theme.marginBase * 4,
    marginBottom: theme.marginBase * 3,
  },
  buttons: {
    marginTop: theme.marginBase * 2,
  },
  subfield: {
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.large,
  },
}));

interface Props {
  invoice: Invoice;
  plan?: Plan;
  billingInformation?: BillingInformation;
  onFinalized: () => void;
}

export const FinalizeInvoiceForm = ({
  invoice,
  plan,
  billingInformation,
  onFinalized,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const toast = useToast();

  const { mutateAsync: finalizeInvoiceMutation } = useFinalizeInvoice(
    invoice.id,
  );

  const shouldSendEmail =
    (!plan?.billingMethod ||
      plan?.billingMethod !== BillingMethod.CREDIT_CARD) &&
    !billingInformation?.requireUseOfSpecificPlatform &&
    invoice.total !== 0;

  const finalizeInvoice = async (values: {
    shouldSendEmail: boolean;
    extraBillingEmails: string[];
  }) => {
    try {
      await finalizeInvoiceMutation(values);
      onFinalized();
    } catch (e) {
      toast.exception(e);
    }
  };

  return (
    <Formik
      initialValues={{
        shouldSendEmail,
        shouldSendRemainingCreditsEmail: invoice.total === 0,
        extraBillingEmails: billingInformation?.extraBillingEmails || [],
      }}
      onSubmit={finalizeInvoice}
    >
      <Form>
        <h3 className={classes.sectionTitle}>
          {t('admin.billing.invoicing.finalizeAndSendStep')}
        </h3>

        <Toggle
          name="shouldSendEmail"
          label={t('admin.billing.invoicing.sendByEmail')}
          noMargin
        />
        <FormikConsumer>
          {({ values: { shouldSendEmail } }) =>
            shouldSendEmail ? (
              <div className={classes.subfield}>
                <ExtraBillingEmailsField
                  name="extraBillingEmails"
                  label={t('admin.billing.invoicing.extraBillingEmails')}
                />
              </div>
            ) : null
          }
        </FormikConsumer>

        <Toggle
          name="shouldSendRemainingCreditsEmail"
          label={t('admin.billing.invoicing.sendRemainingCreditsEmail')}
          explanation={t(
            'admin.billing.invoicing.sendRemainingCreditsEmailExplanation',
          )}
        />

        <SubmitButtonsLine className={classes.buttons}>
          <SubmitButton
            small
            text={t('admin.billing.invoicing.finalizeButton')}
          />
        </SubmitButtonsLine>
      </Form>
    </Formik>
  );
};
